import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { closestCenter, DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { restrictToHorizontalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import type { SortDirection } from '@klareospace/pharos-lib-types';

import { applyColumnOrder } from './apply-column-order';
import { SortableColumnHeader } from '../sortable-column-header';
import { TableLoadingSkeleton } from '../table-loading-skeleton';
import { TablePagination } from '../table-pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { cn } from '../../../lib/utils';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Pointer must move this many pixels before a drag starts — lets a plain click still trigger sort. */
const DRAG_ACTIVATION_DISTANCE_PX = 8;

/** Portal target for DnD-kit's a11y nodes and DragOverlay — neither may render inline inside a `<tr>`. */
const DND_PORTAL_CONTAINER = typeof document === 'undefined' ? undefined : document.body;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DataTableColumn<T> {
  alwaysOn?: boolean;
  cell: (row: T) => React.ReactNode;
  cellClassName?: string;
  className?: string;
  header: React.ReactNode | string;
  key: string;
  sortable?: boolean;
  /** Label for the sort button. Required when header is not a string and sortable is true. */
  sortLabel?: string;
}

export interface DataTablePagination {
  currentPage: number;
  nextLabel: string;
  onPageChange: (page: number) => void;
  /** Fired when the user picks a different page size — omit to hide the selector. */
  onPageSizeChange?: (pageSize: number) => void;
  pageLabel: string;
  pageSize: number;
  /** Label shown next to the page-size selector. Selector visibility depends only on `onPageSizeChange` + `pageSizeOptions`. */
  pageSizeLabel?: string;
  /** Selectable page-size values — omit to hide the selector. */
  pageSizeOptions?: number[];
  previousLabel: string;
  showingText: (startItem: number, endItem: number, totalItems: number) => string;
  totalItems: number;
  totalPages: number;
}

/**
 * Simplified pagination input accepted by table components.
 * Table components convert this to DataTablePagination internally using their own i18n labels.
 */
export interface SimplePagination {
  currentPage: number;
  onPageChange: (page: number) => void;
  /** Fired when the user picks a different page size — omit to hide the selector. */
  onPageSizeChange?: (pageSize: number) => void;
  pageSize: number;
  /** Selectable page-size values — omit to hide the selector. */
  pageSizeOptions?: number[];
  totalItems: number;
  totalPages: number;
}

export interface DataTableSorting {
  onSort: (field: string, direction: SortDirection | null) => void;
  sortBy?: string;
  sortDirection?: SortDirection;
}

/**
 * Enables drag & drop column reordering. `order` is the full, persisted key
 * order (keys absent from it keep their default position). `onOrderChange`
 * fires with the full new key order on drop.
 */
export interface DataTableColumnReorder {
  onOrderChange: (order: string[]) => void;
  order: string[];
}

export interface DataTableProps<T> {
  ariaLabel?: string;
  columns: DataTableColumn<T>[];
  /** Enables drag & drop column reordering — omit for the default static header. */
  columnReorder?: DataTableColumnReorder;
  emptyMessage?: string;
  /** When true, omits the `<thead>` entirely — for compact, embedded tables that have no column headers. Sorting is not compatible with a hidden header. */
  hideHeader?: boolean;
  isLoading?: boolean;
  /** Omit entirely to render the table without a pagination footer */
  pagination?: DataTablePagination;
  /** Optional function to compute additional CSS classes for each row */
  rowClassName?: (row: T) => string | undefined;
  /** Unique key extractor for each row, defaults to index */
  rowKey?: (row: T) => string;
  rows: T[];
  sorting?: DataTableSorting;
  /** Additional CSS classes for the <Table> element */
  tableClassName?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface ItemRange {
  endItem: number;
  startItem: number;
}

function computeItemRange(pagination: DataTablePagination): ItemRange {
  if (pagination.totalItems === 0) {
    return { endItem: 0, startItem: 0 };
  }
  const startItem = (pagination.currentPage - 1) * pagination.pageSize + 1;
  const endItem = Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems);
  return { endItem, startItem };
}

interface DataTableHeaderRowProps<T> {
  columnReorder?: DataTableColumnReorder;
  columns: DataTableColumn<T>[];
  sorting?: DataTableSorting;
}

function DataTableHeaderRow<T>({ columnReorder, columns, sorting }: DataTableHeaderRowProps<T>): React.JSX.Element {
  return (
    <TableHeader className="sticky top-0 z-10 bg-card shadow-[inset_0_-1px_0_0_var(--color-border)]">
      <TableRow>
        {columnReorder ? (
          <ReorderableHeaderCells columnReorder={columnReorder} columns={columns} sorting={sorting} />
        ) : (
          columns.map((column) => <DataTableHeaderCell column={column} key={column.key} sorting={sorting} />)
        )}
      </TableRow>
    </TableHeader>
  );
}

function renderHeaderCellContent<T>(column: DataTableColumn<T>, sorting?: DataTableSorting): React.ReactNode {
  if (sorting && column.sortable) {
    return (
      <SortableColumnHeader
        className="-ms-0 h-auto gap-1 p-0 text-xs font-semibold uppercase text-muted-foreground hover:bg-transparent"
        columnKey={column.key}
        currentSortBy={sorting.sortBy}
        currentSortDirection={sorting.sortDirection}
        label={column.sortLabel ?? (typeof column.header === 'string' ? column.header : column.key)}
        onSort={sorting.onSort}
      />
    );
  }
  return column.header;
}

interface DataTableHeaderCellProps<T> {
  column: DataTableColumn<T>;
  sorting?: DataTableSorting;
}

function DataTableHeaderCell<T>({ column, sorting }: DataTableHeaderCellProps<T>): React.JSX.Element {
  return (
    <TableHead
      className={cn(
        'px-4 py-3 text-start text-xs font-semibold uppercase tracking-wider text-muted-foreground',
        column.className,
      )}
    >
      {renderHeaderCellContent(column, sorting)}
    </TableHead>
  );
}

interface ReorderableHeaderCellsProps<T> {
  columnReorder: DataTableColumnReorder;
  columns: DataTableColumn<T>[];
  sorting?: DataTableSorting;
}

/** Wraps the header cells in a horizontal dnd-kit sortable context. */
function ReorderableHeaderCells<T>({
  columnReorder,
  columns,
  sorting,
}: ReorderableHeaderCellsProps<T>): React.JSX.Element {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX } }),
  );
  const columnKeys = columns.map((column) => column.key);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const activeColumn = columns.find((column) => column.key === activeKey);

  function handleDragStart(event: DragStartEvent): void {
    setActiveKey(String(event.active.id));
  }

  function handleDragEnd(event: DragEndEvent): void {
    setActiveKey(null);
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = columnKeys.indexOf(String(active.id));
    const newIndex = columnKeys.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    columnReorder.onOrderChange(arrayMove(columnKeys, oldIndex, newIndex));
  }

  function handleDragCancel(): void {
    setActiveKey(null);
  }

  // Portaled below — `position: fixed` is viewport-anchored, so this doesn't move where it visually appears.
  const dragOverlay = (
    <DragOverlay>{activeColumn ? <HeaderOverlayChip column={activeColumn} sorting={sorting} /> : null}</DragOverlay>
  );

  return (
    <DndContext
      accessibility={{ container: DND_PORTAL_CONTAINER }}
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      sensors={sensors}
    >
      <SortableContext items={columnKeys} strategy={horizontalListSortingStrategy}>
        {columns.map((column) => (
          <SortableHeaderCell column={column} isReordering={activeKey !== null} key={column.key} sorting={sorting} />
        ))}
      </SortableContext>
      {/* No `dropAnimation` override — dnd-kit's default ~250ms glide + fade is used as-is. */}
      {DND_PORTAL_CONTAINER ? createPortal(dragOverlay, DND_PORTAL_CONTAINER) : dragOverlay}
    </DndContext>
  );
}

/** Floating chip shown while a header is dragged; fills the exact box measured from the source `<th>`. */
function HeaderOverlayChip<T>({ column, sorting }: DataTableHeaderCellProps<T>): React.JSX.Element {
  return (
    <div className="box-border flex h-full w-full cursor-grabbing items-center whitespace-nowrap border bg-card px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground shadow-md">
      {renderHeaderCellContent(column, sorting)}
    </div>
  );
}

interface SortableHeaderCellProps<T> extends DataTableHeaderCellProps<T> {
  /** True from the instant a drag starts until it ends/cancels. */
  isReordering: boolean;
}

function SortableHeaderCell<T>({ column, isReordering, sorting }: SortableHeaderCellProps<T>): React.JSX.Element {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    // Restores the <th>'s implicit "columnheader" role, clobbered by dnd-kit's default "button".
    attributes: { role: 'columnheader', roleDescription: 'sortable column header' },
    // Disables dnd-kit's post-drop layout-change animation to avoid a replayed slide.
    animateLayoutChanges: (): boolean => false,
    id: column.key,
  });

  // Gated on `isReordering` (not dnd-kit's own isDragging/isSorting): dnd-kit reports a stale
  // transform for a moment after drop, which would otherwise slide the cell back into place.
  const style: React.CSSProperties = {
    cursor: 'grab',
    opacity: isDragging ? 0 : 1,
    transform: isDragging || !isReordering ? undefined : CSS.Transform.toString(transform),
    transition: isReordering ? transition : undefined,
  };

  return (
    <TableHead
      className={cn(
        'select-none px-4 py-3 text-start text-xs font-semibold uppercase tracking-wider text-muted-foreground',
        column.className,
      )}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {renderHeaderCellContent(column, sorting)}
    </TableHead>
  );
}

interface DataTableBodyProps<T> {
  columns: DataTableColumn<T>[];
  emptyMessage?: string;
  isLoading: boolean;
  rowClassName?: (row: T) => string | undefined;
  rowKey?: (row: T) => string;
  rows: T[];
}

function DataTableBody<T>({
  columns,
  emptyMessage,
  isLoading,
  rowClassName,
  rowKey,
  rows,
}: DataTableBodyProps<T>): React.JSX.Element {
  return (
    <TableBody>
      {isLoading && <TableLoadingSkeleton columns={columns.length} rows={10} />}
      {!isLoading && rows.length === 0 && (
        <TableRow>
          <TableCell className="text-center text-muted-foreground" colSpan={columns.length}>
            {emptyMessage}
          </TableCell>
        </TableRow>
      )}
      {!isLoading &&
        rows.map((row, index) => (
          <TableRow className={rowClassName?.(row)} key={rowKey ? rowKey(row) : index}>
            {columns.map((column) => (
              <TableCell className={column.cellClassName} key={column.key}>
                {column.cell(row)}
              </TableCell>
            ))}
          </TableRow>
        ))}
    </TableBody>
  );
}

/**
 * Applies a drop's new order synchronously so `DragOverlay` measures the
 * reordered cell instead of a stale one when the consumer's `onOrderChange`
 * updates state asynchronously. Reconciles once `columnReorder.order` itself
 * changes value (compared by value, not reference).
 */
function useOptimisticColumnOrder(
  columnReorder: DataTableColumnReorder | undefined,
): DataTableColumnReorder | undefined {
  const [optimisticOrder, setOptimisticOrder] = useState<string[] | null>(null);
  const confirmedSignature = columnReorder ? columnReorder.order.join(' ') : null;
  const previousSignatureRef = useRef(confirmedSignature);

  useEffect(() => {
    if (previousSignatureRef.current !== confirmedSignature) {
      setOptimisticOrder(null);
      previousSignatureRef.current = confirmedSignature;
    }
  }, [confirmedSignature]);

  if (!columnReorder) {
    return undefined;
  }

  const confirmedColumnReorder = columnReorder;

  function handleOrderChange(newOrder: string[]): void {
    setOptimisticOrder(newOrder);
    confirmedColumnReorder.onOrderChange(newOrder);
  }

  return { onOrderChange: handleOrderChange, order: optimisticOrder ?? confirmedColumnReorder.order };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DataTable<T>({
  ariaLabel,
  columnReorder,
  columns,
  emptyMessage,
  hideHeader = false,
  isLoading = false,
  pagination,
  rowClassName,
  rowKey,
  rows,
  sorting,
  tableClassName,
}: DataTableProps<T>): React.JSX.Element {
  const itemRange = pagination ? computeItemRange(pagination) : null;
  const effectiveColumnReorder = useOptimisticColumnOrder(columnReorder);
  const renderedColumns = effectiveColumnReorder ? applyColumnOrder(columns, effectiveColumnReorder.order) : columns;

  return (
    <>
      <Table
        aria-busy={isLoading}
        aria-label={ariaLabel}
        className={cn('min-w-full w-max', tableClassName)}
        containerClassName="flex-1 min-h-0"
      >
        {!hideHeader && (
          <DataTableHeaderRow columnReorder={effectiveColumnReorder} columns={renderedColumns} sorting={sorting} />
        )}
        <DataTableBody
          columns={renderedColumns}
          emptyMessage={emptyMessage}
          isLoading={isLoading}
          rowClassName={rowClassName}
          rowKey={rowKey}
          rows={rows}
        />
      </Table>
      {pagination && itemRange && (
        <TablePagination
          currentPage={pagination.currentPage}
          nextLabel={pagination.nextLabel}
          onPageChange={pagination.onPageChange}
          onPageSizeChange={pagination.onPageSizeChange}
          pageLabel={pagination.pageLabel}
          pageSize={pagination.pageSize}
          pageSizeLabel={pagination.pageSizeLabel}
          pageSizeOptions={pagination.pageSizeOptions}
          previousLabel={pagination.previousLabel}
          showingText={pagination.showingText(itemRange.startItem, itemRange.endItem, pagination.totalItems)}
          totalPages={pagination.totalPages}
        />
      )}
    </>
  );
}
